import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { SignedIn, SignedOut, RedirectToSignIn } from "@clerk/clerk-react";
import { DEV_AUTH_ENABLED, getDevAuth } from "@/lib/devAuth";
import { Shell } from "@/components/Shell";
import { SignInPage } from "@/routes/SignIn";
import { Dashboard } from "@/routes/Dashboard";
import { JobDetail } from "@/routes/JobDetail";
import { Profile } from "@/routes/Profile";
import { Resumes } from "@/routes/Resumes";
import { Jds } from "@/routes/Jds";
import { JdDetail } from "@/routes/JdDetail";
import { Templates } from "@/routes/Templates";
import { TemplateBuilder } from "@/routes/TemplateBuilder";
import { Reports } from "@/routes/Reports";
import { Settings } from "@/routes/Settings";
import { EditorHarness } from "@/routes/EditorHarness";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  if (DEV_AUTH_ENABLED) {
    if (!getDevAuth()) {
      return <Navigate to="/sign-in" replace state={{ from: location }} />;
    }
    return <>{children}</>;
  }
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/sign-in" element={<SignInPage />} />
      {import.meta.env.DEV && <Route path="/__harness/preview" element={<EditorHarness />} />}
      <Route
        path="/"
        element={
          <RequireAuth>
            <Shell>
              <Dashboard />
            </Shell>
          </RequireAuth>
        }
      />
      <Route
        path="/resumes"
        element={
          <RequireAuth>
            <Shell>
              <Resumes />
            </Shell>
          </RequireAuth>
        }
      />
      <Route
        path="/jobs/:id"
        element={
          <RequireAuth>
            {/* Wide workspace: full content width, sidebar starts collapsed. */}
            <Shell wide>
              <JobDetail />
            </Shell>
          </RequireAuth>
        }
      />
      <Route
        path="/jds"
        element={
          <RequireAuth>
            <Shell>
              <Jds />
            </Shell>
          </RequireAuth>
        }
      />
      <Route
        path="/jds/:id"
        element={
          <RequireAuth>
            <Shell wide>
              <JdDetail />
            </Shell>
          </RequireAuth>
        }
      />
      <Route
        path="/profile"
        element={
          <RequireAuth>
            <Shell>
              <Profile />
            </Shell>
          </RequireAuth>
        }
      />
      <Route
        path="/templates"
        element={
          <RequireAuth>
            <Shell>
              <Templates />
            </Shell>
          </RequireAuth>
        }
      />
      <Route
        path="/templates/:rowId"
        element={
          <RequireAuth>
            {/* Wide workspace: full content width, sidebar starts collapsed. */}
            <Shell wide>
              <TemplateBuilder />
            </Shell>
          </RequireAuth>
        }
      />
      <Route
        path="/reports"
        element={
          <RequireAuth>
            <Shell>
              <Reports />
            </Shell>
          </RequireAuth>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireAuth>
            <Shell>
              <Settings />
            </Shell>
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
